import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { partnersService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PartnerApplicationsTable = ({ status = 'pending', title = 'Partner Başvuruları' }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: status,
        ...filters
      };

      const response = await partnersService.getAllPartnerApplications(params);

      if (response.success) {
        setApplications(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Başvurular yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Başvurular yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [status, pagination.page, pagination.limit, filters]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleApprove = async (application) => {
    if (!window.confirm(`${application.name} adlı partner başvurusunu onaylamak istediğinize emin misiniz?`)) {
      return;
    }

    const response = await partnersService.approvePartnerApplication(application.id);
    if (response.success) {
      toast.success('Partner başvurusu onaylandı');
      loadApplications();
    } else {
      toast.error(response.error || 'Başvuru onaylanamadı');
    }
  };

  const handleReject = (application) => {
    setSelectedApplication(application);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!selectedApplication) return;

    const response = await partnersService.rejectPartnerApplication(selectedApplication.id, rejectionReason);
    if (response.success) {
      toast.success('Partner başvurusu reddedildi');
      setShowRejectModal(false);
      setSelectedApplication(null);
      loadApplications();
    } else {
      toast.error(response.error || 'Başvuru reddedilemedi');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="badge bg-warning">Beklemede</span>,
      approved: <span className="badge bg-success">Onaylandı</span>,
      rejected: <span className="badge bg-danger">Reddedildi</span>
    };
    return badges[status] || <span className="badge bg-secondary">{status}</span>;
  };

  return (
    <>
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Göster</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={pagination.limit}
              onChange={handlePageSizeChange}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                name="search"
                placeholder="Ara (Ad, Telefon, Email, Şirket)..."
                value={filters.search}
                onChange={handleSearch}
              />
            </form>
          </div>
        </div>

        {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Başvuru bulunamadı</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="bg-light">
                  <tr>
                    <th>S.L</th>
                    <th>Başvuru Tarihi</th>
                    <th>Ad Soyad</th>
                    <th>Telefon</th>
                    <th>Email</th>
                    <th>Şirket Adı</th>
                    <th>Vergi No</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app, index) => (
                    <tr key={app.id}>
                      <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                      <td>{formatDate(app.created_at)}</td>
                      <td>{app.name}</td>
                      <td>{app.phone}</td>
                      <td>{app.email || '-'}</td>
                      <td>{app.company_name || '-'}</td>
                      <td>{app.tax_number || '-'}</td>
                      <td>{getStatusBadge(app.status)}</td>
                      <td>
                        {app.status === 'pending' && (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleApprove(app)}
                              title="Onayla"
                            >
                              <Icon icon="mdi:check" />
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleReject(app)}
                              title="Reddet"
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          </div>
                        )}
                        {app.status === 'rejected' && app.rejection_reason && (
                          <span className="text-danger" title={app.rejection_reason}>
                            <Icon icon="mdi:information" />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {pagination.totalPages > 1 && (
          <div className="card-footer border-top bg-base py-16 px-24">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-sm text-secondary-light">
                  Toplam {pagination.total} başvuru, Sayfa {pagination.page} / {pagination.totalPages}
                </span>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Önceki
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Partner Başvurusunu Reddet</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedApplication(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <p>Reddetme sebebini girin:</p>
                <textarea
                  className="form-control"
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Reddetme sebebi..."
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedApplication(null);
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmReject}
                >
                  Reddet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PartnerApplicationsTable;

